var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { merge } from "lodash";
import * as React from "react";
import Slide from "@material-ui/core/Slide";
import { fade } from "@material-ui/core/styles/colorManipulator";
import Transitions from "@material-ui/core/styles/transitions";
import { ChevronDownIcon, XIcon } from "../icon";
import { globalStyles } from "./global-styles";
import { round } from "./typography";
var SnackbarTransition = function (props) { return React.createElement(Slide, __assign({}, props, { direction: "left" })); };
var SWITCH_DIMENSIONS = {
    heightPx: 24,
    widthPx: 44,
    thumbSizePx: 16,
};
export var createOverrides = function (_a) {
    var _b, _c, _d;
    var props = _a.props, overrides = _a.overrides, options = __rest(_a, ["props", "overrides"]);
    var spacing = options.spacing, palette = options.palette, breakpoints = options.breakpoints, typography = options.typography, mixins = options.mixins, layout = options.layout;
    var customProps = typeof props === "function" ? props(options) : props;
    var customOverrides = typeof overrides === "function" ? overrides(options) : overrides;
    var getSelectionControlOverrides = function (focusRingBorderRadius) { return ({
        root: {
            padding: 0,
            transition: Transitions.create(["background-color", "box-shadow", "border-color", "color"], {
                duration: Transitions.duration.short,
            }),
            color: mixins.shadeOfColor("grey", "light"),
            "&:focus-within svg": __assign({ borderRadius: focusRingBorderRadius }, mixins.focusRing()),
        },
        colorPrimary: {
            "&:hover,": {
                backgroundColor: "transparent",
            },
            "&$disabled": {
                backgroundColor: "transparent",
            },
            "&:hover:not($checked)": {
                color: mixins.shadeOfColor("neutral", "dark"),
                "& svg :last-child": {
                    fill: mixins.shadeOfColor("neutral", "lightest"),
                },
            },
            "&$checked": {
                color: palette.primary.main,
                "&:hover": {
                    backgroundColor: "transparent",
                },
            },
            "&$checked$disabled": {
                color: mixins.shadeOfColor("neutral", "base"),
            },
            "&$disabled:not($checked)": {
                color: mixins.shadeOfColor("neutral", "light"),
                "& svg :last-child": {
                    fill: mixins.shadeOfColor("neutral", "lightest"),
                },
            },
        },
    }); };
    var defaultOverrides = {
        props: {
            MuiAppBar: {
                position: "fixed",
                color: "default",
            },
            MuiButtonBase: {
                disableRipple: true,
            },
            MuiButton: {
                size: "large",
                disableElevation: true,
            },
            MuiButtonGroup: {
                disableRipple: true,
                disableElevation: true,
            },
            MuiCard: {
                variant: "outlined",
            },
            MuiCheckbox: {
                color: "primary",
            },
            MuiChip: {
                variant: "outlined",
                deleteIcon: React.createElement(XIcon, null),
            },
            MuiExpansionPanel: {
                variant: "outlined",
            },
            MuiExpansionPanelSummary: {
                expandIcon: React.createElement(ChevronDownIcon, null),
            },
            MuiFormControl: {
                fullWidth: false,
                variant: "outlined",
            },
            MuiFormHelperText: {
                variant: "outlined",
            },
            MuiInputBase: {
                color: "primary",
            },
            MuiInputLabel: {
                shrink: true,
                variant: "outlined",
            },
            MuiMenu: {
                anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "left",
                },
                transformOrigin: {
                    vertical: "top",
                    horizontal: "left",
                },
                getContentAnchorEl: null,
            },
            MuiOutlinedInput: {
                fullWidth: false,
                notched: false,
            },
            MuiRadio: {
                color: "primary",
            },
            MuiSelect: {
                native: true,
                variant: "outlined",
            },
            MuiSnackbar: {
                TransitionComponent: SnackbarTransition,
                anchorOrigin: {
                    vertical: "top",
                    horizontal: "right",
                },
            },
            MuiSwitch: {
                color: "primary",
                size: "medium",
            },
            MuiTableSortLabel: {
                IconComponent: ChevronDownIcon,
            },
            MuiTabs: {
                indicatorColor: "primary",
                textColor: "primary",
            },
            MuiTextField: {
                SelectProps: {
                    native: true,
                },
                variant: "outlined",
                fullWidth: false,
            },
            MuiTooltip: {
                arrow: true,
            },
        },
        overrides: {
            MuiAlert: {
                root: {
                    backgroundColor: palette.background.paper,
                    "& a,& a:hover": {
                        color: "currentColor",
                        textDecoration: "underline",
                    },
                    // this is needed due to some cosmos global styles bleeding through. The tabs from cosmos set all ul's to have no list-style.
                    // This should be moved into the message override below once the new UI and theme are active.
                    "& $message > ul": {
                        listStyle: "disc",
                        marginTop: spacing(0.5),
                        paddingLeft: spacing(2),
                    },
                },
                message: __assign(__assign({}, typography.body2), { padding: spacing(0.75, 0) }),
                standardError: {
                    borderLeft: "8px solid " + palette.error.main,
                    color: palette.error.dark,
                    backgroundColor: palette.error.light,
                    "& $icon": {
                        color: palette.error.dark,
                    },
                },
                standardSuccess: {
                    borderLeft: "8px solid " + palette.success.main,
                    color: palette.success.dark,
                    backgroundColor: palette.success.light,
                    "& $icon": {
                        color: palette.success.dark,
                    },
                },
                standardWarning: {
                    borderLeft: "8px solid " + palette.warning.main,
                    color: palette.warning.dark,
                    backgroundColor: palette.warning.light,
                    "& $icon": {
                        color: palette.warning.dark,
                    },
                },
                standardInfo: {
                    borderLeft: "8px solid " + palette.info.main,
                    color: palette.info.dark,
                    backgroundColor: palette.info.light,
                    "& $icon": {
                        color: palette.info.dark,
                    },
                },
                outlinedError: {
                    border: "1px solid " + palette.border.dark,
                    borderLeft: "8px solid " + palette.error.main,
                    color: palette.text.primary,
                    "& $icon": {
                        color: mixins.shadeOfColor("red", "dark"),
                    },
                },
                outlinedSuccess: {
                    border: "1px solid " + palette.border.dark,
                    borderLeft: "8px solid " + palette.success.main,
                    color: palette.text.primary,
                    "& $icon": {
                        color: mixins.shadeOfColor("green", "dark"),
                    },
                },
                outlinedWarning: {
                    border: "1px solid " + palette.border.dark,
                    borderLeft: "8px solid " + palette.warning.main,
                    color: palette.text.primary,
                    "& $icon": {
                        color: mixins.shadeOfColor("yellow", "dark"),
                    },
                },
                outlinedInfo: {
                    border: "1px solid " + palette.border.dark,
                    borderLeft: "8px solid " + palette.info.main,
                    color: palette.text.primary,
                    "& $icon": {
                        color: mixins.shadeOfColor("blue", "dark"),
                    },
                },
                action: {
                    paddingTop: 5,
                    alignSelf: "flex-start",
                },
            },
            MuiAlertTitle: {
                root: __assign(__assign({}, typography.subtitle2), { display: "inline", marginRight: spacing(0.5) }),
            },
            MuiAppBar: {
                root: {
                    boxShadow: "none",
                    height: layout.appBarHeight,
                    zIndex: 20,
                },
                positionFixed: {
                    top: layout.appTop,
                },
                colorDefault: {
                    backgroundColor: mixins.shadeOfColor("neutral", "darkest"),
                    color: mixins.shadeOfColor("neutral", "light"),
                },
            },
            MuiAutocomplete: {
                inputRoot: {
                    minHeight: 40,
                    // https://github.com/mui-org/material-ui/blob/master/packages/material-ui-lab/src/Autocomplete/Autocomplete.js#L79
                    '&[class*="MuiOutlinedInput-root"]': {
                        padding: spacing(0.25, 1, 0.25, 0.5),
                        "& $input": {
                            padding: spacing(0.5, 1),
                            "&:first-child": {
                                paddingLeft: spacing(1.75),
                            },
                        },
                        "& $endAdornment": {
                            right: spacing(0.75),
                        },
                    },
                },
                tag: {
                    margin: 1,
                },
                endAdornment: {
                    top: "unset",
                },
            },
            MuiAvatar: {
                circle: {
                    color: mixins.shadeOfColor("neutral", "dark"),
                    backgroundColor: mixins.shadeOfColor("neutral", "lightest"),
                },
                rounded: {
                    borderRadius: 4,
                    color: mixins.shadeOfColor("neutral", "dark"),
                    backgroundColor: mixins.shadeOfColor("neutral", "lightest"),
                },
                colorDefault: {
                    color: mixins.shadeOfColor("neutral", "dark"),
                    backgroundColor: mixins.shadeOfColor("neutral", "lightest"),
                },
            },
            MuiBackdrop: {
                root: {
                    backgroundColor: fade(mixins.shadeOfColor("neutral", "darkest"), 0.7),
                },
            },
            MuiBreadcrumbs: {
                separator: {
                    color: mixins.shadeOfColor("neutral", "light"),
                    fontSize: "0.875rem",
                },
            },
            MuiButton: {
                root: {
                    padding: spacing(0.625, 1.5),
                    color: palette.text.primary,
                    width: "fit-content",
                    "&$disabled": {
                        color: palette.text.secondary,
                        backgroundColor: mixins.shadeOfColor("neutral", "lighter"),
                    },
                    "&:hover,&.Mui-hover": {
                        backgroundColor: mixins.shadeOfColor("neutral", "lightest"),
                    },
                    "&:active,&.Mui-active": {
                        backgroundColor: mixins.shadeOfColor("neutral", "lighter"),
                    },
                },
                sizeLarge: {
                    fontSize: typography.button.fontSize,
                },
                sizeSmall: {
                    minWidth: "unset",
                    fontSize: typography.button.fontSize,
                },
                contained: {
                    color: palette.text.primary,
                    backgroundColor: mixins.shadeOfColor("neutral", "lighter"),
                    "&:hover,&.Mui-hover": {
                        backgroundColor: mixins.shadeOfColor("neutral", "light"),
                    },
                    "&:active,&.Mui-active": {
                        backgroundColor: mixins.shadeOfColor("neutral", "base"),
                    },
                    "&$disabled": {
                        color: palette.text.secondary,
                        backgroundColor: mixins.shadeOfColor("neutral", "lighter"),
                    },
                },
                containedPrimary: {
                    "&:hover,&.Mui-hover": {
                        backgroundColor: palette.primary[700],
                    },
                    "&:active,&.Mui-active": {
                        backgroundColor: palette.primary[800],
                    },
                },
                containedSecondary: {
                    "&:hover,&.Mui-hover": {
                        backgroundColor: palette.primary[700] || palette.secondary.dark,
                    },
                    "&:active,&.Mui-active": {
                        backgroundColor: palette.primary[800] || palette.secondary.dark,
                    },
                },
                containedSizeLarge: {
                    padding: spacing(1, 2),
                },
                containedSizeSmall: {
                    padding: spacing(0.375, 1.25),
                },
                outlined: {
                    padding: spacing(0.5, 1.375),
                    borderColor: palette.border.dark,
                    backgroundColor: palette.background.paper,
                    "&$disabled": {
                        borderColor: palette.border.dark,
                    },
                },
                outlinedSizeSmall: {
                    padding: spacing(0.25, 1.125),
                },
                outlinedSizeLarge: {
                    padding: spacing(0.875, 1.875),
                },
                outlinedPrimary: {
                    "&:hover,&.Mui-hover": {
                        backgroundColor: palette.primary[50],
                    },
                    "&:active,&.Mui-active": {
                        backgroundColor: palette.primary[100],
                    },
                    "&$disabled": {
                        borderColor: palette.border.dark,
                    },
                },
                outlinedSecondary: {
                    "&:hover,&.Mui-hover": {
                        backgroundColor: palette.secondary[50] || fade(palette.secondary.main, palette.action.hoverOpacity),
                    },
                    "&:active,&.Mui-active": {
                        backgroundColor: palette.secondary[100] || palette.secondary.light,
                    },
                    "&$disabled": {
                        borderColor: palette.border.dark,
                    },
                },
                text: {
                    padding: spacing(0.625, 1.5),
                },
                textSizeSmall: {
                    padding: spacing(0.375, 1.25),
                },
                textSizeLarge: {
                    padding: spacing(1, 2),
                },
                textPrimary: {
                    "&:hover,&.Mui-hover": {
                        backgroundColor: palette.primary[50],
                    },
                    "&:active,&.Mui-active": {
                        backgroundColor: palette.primary[100],
                    },
                },
                textSecondary: {
                    "&:hover,&.Mui-hover": {
                        backgroundColor: palette.secondary[50] || fade(palette.secondary.main, palette.action.hoverOpacity),
                    },
                    "&:active,&.Mui-active": {
                        backgroundColor: palette.secondary[100] || palette.secondary.light,
                    },
                },
                iconSizeLarge: {
                    "& > *:first-child": {
                        fontSize: "1rem",
                        height: "1em",
                        width: "1em",
                    },
                },
                iconSizeMedium: {
                    "& > *:first-child": {
                        fontSize: "1rem",
                        height: "1em",
                        width: "1em",
                    },
                },
                iconSizeSmall: {
                    "& > *:first-child": {
                        fontSize: "1rem",
                        height: "1em",
                        width: "1em",
                    },
                },
                disableElevation: {
                    "&:focus,&$focusVisible": mixins.focusRing(),
                },
            },
            MuiCardHeader: {
                root: {
                    marginBottom: spacing(3),
                    padding: 0,
                    "&:last-child": {
                        marginBottom: 0,
                    },
                },
                content: {
                    minWidth: spacing(0),
                },
                avatar: {
                    alignSelf: "flex-start",
                    paddingTop: spacing(0.5),
                },
                title: {
                    marginBottom: spacing(0.5),
                    "&:last-child": {
                        marginBottom: 0,
                    },
                },
                action: {
                    marginRight: 0,
                    marginTop: 0,
                    alignSelf: "center",
                },
                subheader: __assign(__assign({}, typography.body2), { color: palette.text.secondary }),
            },
            MuiCheckbox: getSelectionControlOverrides(4),
            MuiChip: {
                root: {
                    color: palette.text.primary,
                    height: "unset",
                    borderRadius: 4,
                    "&$disabled": {
                        opacity: 1,
                        backgroundColor: mixins.shadeOfColor("neutral", "lighter"),
                    },
                    "&:hover": {
                        cursor: "pointer",
                        boxShadow: "none",
                        backgroundColor: mixins.shadeOfColor("neutral", "lightest"),
                    },
                },
                label: {
                    paddingTop: spacing(1.25),
                    paddingBottom: spacing(1.25),
                    paddingLeft: spacing(1),
                },
                sizeSmall: {
                    height: "unset",
                },
                labelSmall: {
                    padding: spacing(0.5, 1),
                },
                outlined: {
                    "$clickable&:focus, $deletable&:focus": {
                        backgroundColor: "transparent",
                    },
                    "$clickable&:hover, $deletable&:hover": {
                        backgroundColor: mixins.shadeOfColor("neutral", "lightest"),
                    },
                },
                deletable: {
                    "&:hover": {
                        backgroundColor: mixins.shadeOfColor("neutral", "lightest"),
                    },
                    "&:focus": mixins.focusRing(),
                },
                clickable: {
                    "&:hover": {
                        backgroundColor: mixins.shadeOfColor("neutral", "lightest"),
                    },
                    "&:focus": mixins.focusRing(),
                },
                deleteIcon: {
                    height: "1rem",
                    width: "1rem",
                    color: palette.text.secondary,
                },
            },
            MuiCssBaseline: {
                "@global": globalStyles({ typography: typography }),
            },
            MuiDialog: {
                paperWidthMd: {
                    "max-width": spacing(100),
                },
                paperWidthSm: {
                    "max-width": spacing(80),
                },
                paperWidthXs: {
                    "max-width": spacing(56),
                },
            },
            MuiDialogTitle: {
                root: {
                    padding: spacing(5, 5, 3, 5),
                },
            },
            MuiDialogContent: {
                root: {
                    padding: spacing(0.5, 5),
                    "&:first-child": {
                        paddingTop: spacing(3),
                    },
                    "&:last-child": {
                        paddingBottom: spacing(3),
                    },
                },
            },
            MuiDialogActions: {
                root: {
                    padding: spacing(3, 5, 5, 5),
                },
            },
            MuiExpansionPanel: {
                root: {
                    padding: spacing(4),
                    margin: 0,
                    "&$expanded": {
                        marginTop: 0,
                        marginBottom: 0,
                    },
                    "&:before": {
                        display: "none",
                    },
                },
                rounded: {
                    borderRadius: 6,
                },
            },
            MuiExpansionPanelSummary: {
                root: {
                    padding: 0,
                    minHeight: "unset",
                    "&:focus": {
                        "& $expandIcon": mixins.focusRing(),
                    },
                    "&$focused": {
                        backgroundColor: "transparent",
                    },
                    "&$expanded": {
                        minHeight: "unset",
                    },
                },
                content: {
                    margin: 0,
                    "&$expanded": {
                        margin: 0,
                    },
                },
            },
            MuiExpansionPanelDetails: {
                root: {
                    padding: 0,
                    flexDirection: "column",
                    marginTop: spacing(3),
                },
            },
            MuiFormControl: {
                root: {
                    maxWidth: 400,
                },
                fullWidth: {
                    maxWidth: "100%",
                },
            },
            MuiFormControlLabel: {
                root: {
                    marginLeft: -spacing(1),
                },
                label: __assign({}, typography.label),
            },
            MuiFormHelperText: {
                root: __assign({ marginTop: spacing(1) }, typography.body2),
                contained: {
                    marginLeft: 0,
                    marginRight: 0,
                },
                marginDense: {
                    marginTop: spacing(1),
                },
            },
            MuiFormLabel: {
                root: __assign(__assign({}, typography.subtitle2), { color: palette.text.primary, marginBottom: spacing(1), "&$focused&:not($error), &$disabled": {
                        color: palette.text.primary,
                    } }),
                asterisk: {
                    color: palette.error.main,
                },
            },
            MuiIconButton: {
                root: {
                    padding: 0,
                    fontSize: typography.pxToRem(16),
                    height: typography.pxToRem(34),
                    width: typography.pxToRem(34),
                    borderRadius: 4,
                    color: palette.text.primary,
                    transition: Transitions.create(["background-color", "box-shadow", "border-color", "color"], {
                        duration: Transitions.duration.short,
                    }),
                    "&$disabled": {
                        backgroundColor: mixins.shadeOfColor("neutral", "lighter"),
                        color: palette.text.secondary,
                    },
                    "&:hover,&.Mui-hover": {
                        backgroundColor: mixins.shadeOfColor("neutral", "lightest"),
                    },
                    "&:active,&.Mui-active": {
                        backgroundColor: mixins.shadeOfColor("neutral", "lighter"),
                    },
                },
                label: {
                    "& > svg:first-child": {
                        height: "1em",
                        width: "1em",
                    },
                },
                sizeSmall: {
                    fontSize: typography.pxToRem(16),
                    height: typography.pxToRem(30),
                    width: typography.pxToRem(30),
                },
                colorPrimary: {
                    "&:hover,&.Mui-hover": {
                        backgroundColor: palette.primary[50],
                    },
                    "&:active,&.Mui-active": {
                        backgroundColor: palette.primary[100],
                    },
                },
                colorSecondary: {
                    "&:hover,&.Mui-hover": {
                        backgroundColor: palette.secondary.light,
                    },
                    "&:active,&.Mui-active": {
                        backgroundColor: palette.secondary.light,
                    },
                },
                colorInherit: {
                    "&:hover,&.Mui-hover": {
                        backgroundColor: "rgba(0,0,0, 0.04)",
                    },
                    "&:active,&.Mui-active": {
                        backgroundColor: "rgba(0,0,0, 0.1)",
                    },
                },
                edgeEnd: {
                    marginRight: -11,
                },
            },
            MuiInputAdornment: {
                root: {
                    color: palette.text.secondary,
                },
                positionEnd: {
                    marginLeft: spacing(1.25),
                    "&$marginDense": {
                        marginRight: -2,
                    },
                },
            },
            MuiInputBase: {
                root: __assign(__assign({}, typography.input), { "&$disabled": {
                        backgroundColor: palette.action.disabledBackground,
                        color: palette.text.primary,
                    } }),
                inputMultiline: __assign({}, typography.body2),
                input: {
                    // use line height and padding for height
                    height: "unset",
                    padding: spacing(1, 2),
                },
                // wrapper around input size="small"
                marginDense: {
                    margin: 0,
                },
                inputMarginDense: {
                    paddingTop: 5,
                    paddingBottom: 5,
                },
            },
            MuiInputLabel: {
                formControl: {
                    position: "static",
                },
                outlined: {
                    marginBottom: spacing(1),
                    "&$shrink": {
                        transform: "none",
                    },
                },
                asterisk: {
                    color: palette.error.main,
                },
            },
            MuiLink: {
                root: {
                    display: "inline",
                    alignItems: "center",
                    "& > svg": {
                        width: "1em",
                        height: "1em",
                        "&:first-child": {
                            marginRight: spacing(1),
                        },
                    },
                },
                button: {
                    display: "inline-flex",
                    verticalAlign: "initial",
                },
            },
            MuiList: {
                root: {
                    outline: 0,
                },
            },
            MuiListItem: {
                root: {
                    paddingTop: spacing(1.25),
                    paddingBottom: spacing(1.25),
                    borderTop: "1px solid " + palette.divider,
                    "&:last-child": {
                        borderBottom: "1px solid " + palette.divider,
                    },
                    "&$selected": {
                        backgroundColor: mixins.shadeOfColor("neutral", "lighter"),
                    },
                },
                button: {
                    "&:hover": {
                        backgroundColor: mixins.shadeOfColor("neutral", "lightest"),
                    },
                },
            },
            MuiListItemText: {
                primary: {
                    fontSize: typography.pxToRem(14),
                    fontWeight: typography.fontWeightBold,
                },
                secondary: {
                    fontSize: typography.pxToRem(13),
                },
            },
            MuiListSubheader: {
                sticky: {
                    backgroundColor: palette.background.paper,
                },
            },
            MuiMenu: {
                paper: {
                    border: "1px solid " + palette.border.dark,
                    minWidth: 175,
                },
            },
            MuiMenuItem: {
                root: __assign(__assign({}, typography.body2), { height: 32, border: "none", "&:last-child": {
                        borderBottom: "none",
                    } }),
            },
            MuiMobileStepper: {
                dot: {
                    margin: "0 " + spacing(0.5) + "px",
                },
            },
            MuiOutlinedInput: {
                root: {
                    backgroundColor: palette.background.paper,
                    "& $notchedOutline": {
                        borderColor: mixins.shadeOfColor("neutral", "light"),
                    },
                    "&$disabled$error $notchedOutline": {
                        borderColor: palette.error.main,
                    },
                    "&:hover $notchedOutline": {
                        borderColor: mixins.shadeOfColor("neutral", "dark"),
                    },
                    "&$focused $notchedOutline": __assign({ borderWidth: 2 }, mixins.focusRing()),
                    "&$error $notchedOutline": {
                        borderWidth: 2,
                    },
                    "&$error&$focused $notchedOutline": __assign({}, mixins.focusRing({ color: palette.error.main })),
                },
                notchedOutline: {
                    top: 0,
                    transition: Transitions.create(["box-shadow", "border-color", "border-width"], {
                        duration: Transitions.duration.shortest,
                    }),
                    "& legend": {
                        display: "none",
                    },
                },
                input: {
                    padding: spacing(1, 2),
                },
                multiline: {
                    padding: 0,
                    "&$adornedEnd": {
                        // resetting the default
                        paddingRight: 14,
                    },
                },
                inputMultiline: {
                    padding: spacing(1, 2),
                    "&$inputAdornedEnd": {
                        // resetting the default
                        paddingRight: spacing(1),
                    },
                },
                inputMarginDense: {
                    paddingTop: 5,
                    paddingBottom: 5,
                    paddingLeft: spacing(1.5),
                    paddingRight: spacing(1.5),
                    "&[type='number']": {
                        paddingRight: spacing(0.5),
                    },
                },
            },
            MuiRadio: getSelectionControlOverrides("50%"),
            MuiSelect: {
                selectMenu: {
                    minHeight: round(24 / 14) + "em",
                    lineHeight: round(24 / 14) + "em",
                },
                select: {
                    "&:focus": {
                        backgroundColor: palette.background.paper,
                    },
                },
            },
            MuiSnackbar: {
                root: {
                    maxWidth: typography.pxToRem(640),
                },
                anchorOriginTopRight: (_b = {},
                    _b[breakpoints.up("sm")] = {
                        top: layout.appBarHeight + layout.appTop + spacing(3),
                    },
                    _b),
                anchorOriginTopCenter: (_c = {},
                    _c[breakpoints.up("sm")] = {
                        top: layout.appBarHeight + layout.appTop + spacing(3),
                    },
                    _c),
            },
            MuiSwitch: {
                root: {
                    width: typography.pxToRem(SWITCH_DIMENSIONS.widthPx),
                    height: typography.pxToRem(SWITCH_DIMENSIONS.heightPx),
                    padding: 0,
                    overflow: "visible",
                    "&:focus-within,&.Mui-focused": {
                        "& $track": mixins.focusRing(),
                    },
                },
                colorPrimary: {
                    "&$checked": {
                        color: palette.common.white,
                        "& + $track": {
                            backgroundColor: mixins.shadeOfColor("green", "base"),
                        },
                        "&$disabled": {
                            color: palette.common.white,
                            "& + $track": {
                                backgroundColor: mixins.shadeOfColor("green", "lighter"),
                            },
                        },
                    },
                    "&$disabled": {
                        color: palette.common.white,
                        "& + $track": {
                            opacity: 1,
                            backgroundColor: mixins.shadeOfColor("neutral", "lighter"),
                        },
                    },
                },
                track: {
                    opacity: 1,
                    backgroundColor: mixins.shadeOfColor("neutral", "light"),
                    borderRadius: typography.pxToRem(SWITCH_DIMENSIONS.heightPx * 2),
                },
                switchBase: {
                    borderRadius: "50%",
                    padding: typography.pxToRem((SWITCH_DIMENSIONS.heightPx - SWITCH_DIMENSIONS.thumbSizePx) / 2),
                    height: "unset",
                    width: "unset",
                    "&$checked": {
                        transform: "translateX(" + typography.pxToRem(SWITCH_DIMENSIONS.thumbSizePx + 4) + ")",
                        "& + $track": {
                            opacity: 1,
                        },
                    },
                    "&$disabled,&:hover,&:active": {
                        backgroundColor: "transparent",
                    },
                },
                thumb: {
                    height: typography.pxToRem(SWITCH_DIMENSIONS.thumbSizePx),
                    width: typography.pxToRem(SWITCH_DIMENSIONS.thumbSizePx),
                    borderRadius: typography.pxToRem(SWITCH_DIMENSIONS.thumbSizePx),
                },
            },
            MuiTable: {
                root: {
                    // this allows the table to scroll horizontally on smaller screens
                    minWidth: 400,
                    "& caption": __assign({}, typography.caption),
                },
            },
            MuiTableCell: {
                root: __assign({ borderBottom: "1px solid " + palette.divider, padding: spacing(2) }, typography.body2),
                head: __assign({ padding: spacing(1, 2), color: palette.text.primary }, typography.subtitle2),
            },
            MuiTableRow: {
                root: {
                    transition: Transitions.create(["background-color"], { duration: Transitions.duration.shortest }),
                    "&$hover:hover": {
                        backgroundColor: fade(mixins.shadeOfColor("neutral", "lightest"), 0.5),
                    },
                },
                head: {
                    "&$hover:hover": {
                        backgroundColor: "transparent",
                    },
                },
            },
            MuiTableContainer: {
                root: {
                    position: "relative",
                },
            },
            MuiTableSortLabel: {
                active: {
                    color: palette.text.primary,
                },
                icon: {
                    fontSize: "inherit",
                    height: "1em",
                    width: "1em",
                },
                root: {
                    "&$active": {
                        "&& $icon": {
                            color: palette.text.primary,
                        },
                    },
                },
            },
            MuiDivider: {
                root: {
                    backgroundColor: palette.divider,
                },
                light: {
                    backgroundColor: palette.divider,
                },
            },
            MuiTab: {
                root: (_d = {
                        paddingLeft: 0,
                        paddingRight: 0,
                        margin: spacing(0, 2),
                        minWidth: "unset"
                    },
                    _d[breakpoints.up("sm")] = {
                        minWidth: "unset",
                    },
                    _d["&:first-child"] = {
                        marginLeft: 0,
                    },
                    _d),
                textColorPrimary: {
                    "&:hover": {
                        color: palette.primary.dark,
                    },
                    "&:focus": {
                        color: palette.primary.main,
                    },
                    color: palette.text.secondary,
                    fontWeight: typography.fontWeightRegular,
                },
            },
            MuiTabs: {
                scroller: {
                    borderBottom: "1px solid " + palette.divider,
                },
            },
            MuiToggleButton: {
                root: {
                    borderColor: palette.neutral[200],
                    "& > *:first-child > svg": {
                        stroke: palette.neutral[900],
                    },
                    "&$selected": {
                        backgroundColor: palette.neutral[50],
                        "& > *:first-child > svg": {
                            stroke: palette.primary.main,
                        },
                    },
                    "&$disabled": {
                        "& > *:first-child > svg": {
                            stroke: palette.neutral[200],
                        },
                    },
                },
                sizeSmall: {
                    minWidth: "unset",
                    fontSize: typography.button.fontSize,
                    padding: spacing(1.125, 1.125),
                    "& > *:first-child": {
                        fontSize: "1rem",
                        height: "1em",
                        width: "1em",
                    },
                },
            },
            MuiTooltip: {
                tooltip: __assign(__assign({}, typography.caption), { fontWeight: typography.fontWeightMedium, color: palette.common.white, backgroundColor: palette.common.black, padding: spacing(1, 1.5) }),
                arrow: {
                    color: palette.common.black,
                },
            },
        },
    };
    return merge(defaultOverrides, { props: customProps, overrides: customOverrides });
};
